/**
 * Inline SVG icons for lab apparatus.
 *
 * One component, one switch over `id`, all paths drawn in the current
 * `text-foreground` colour using stroke-only geometry so they sit cleanly
 * on dark backgrounds and inherit the design system. No external assets,
 * no raster files - keeps the bundle small and themable.
 *
 * All icons live inside a 96×96 viewBox so they share visual weight.
 */

type Props = {
  id: string;
  size?: number;
  className?: string;
};

export default function GlasswareIcon({ id, size = 120, className }: Props) {
  return (
    <svg
      role="img"
      aria-hidden="true"
      viewBox="0 0 96 96"
      width={size}
      height={size}
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {renderShape(id)}
    </svg>
  );
}

function renderShape(id: string): JSX.Element {
  switch (id) {
    case "beaker":
      return (
        <g>
          {/* Body with pour spout */}
          <path d="M22 22 L22 74 Q22 84 32 84 L62 84 Q72 84 72 74 L72 22" />
          <path d="M20 22 L74 22" />
          <path d="M72 22 Q76 22 78 26" />
          {/* Graduations */}
          <path d="M28 36 L34 36" />
          <path d="M28 50 L34 50" />
          <path d="M28 64 L34 64" />
          {/* Liquid line */}
          <path d="M22 58 Q47 54 72 58" opacity="0.6" />
        </g>
      );
    case "erlenmeyer-flask":
      return (
        <g>
          <path d="M38 14 L58 14" />
          <path d="M40 14 L40 32 L20 76 Q18 84 28 84 L68 84 Q78 84 76 76 L56 32 L56 14" />
          <path d="M28 64 Q48 60 68 64" opacity="0.6" />
        </g>
      );
    case "volumetric-flask":
      return (
        <g>
          <path d="M40 12 L56 12" />
          <path d="M42 12 L42 38 Q22 50 22 68 Q22 84 48 84 Q74 84 74 68 Q74 50 54 38 L54 12" />
          {/* Calibration line */}
          <path d="M30 52 L66 52" strokeDasharray="3 2" />
          <path d="M28 64 Q48 60 68 64" opacity="0.6" />
        </g>
      );
    case "florence-flask":
      return (
        <g>
          <path d="M40 12 L56 12" />
          <path d="M42 12 L42 32 Q22 42 22 60 Q22 80 38 84 L58 84 Q74 80 74 60 Q74 42 54 32 L54 12" />
          {/* Flat bottom */}
          <path d="M34 84 L62 84" />
          <path d="M28 60 Q48 56 68 60" opacity="0.6" />
        </g>
      );
    case "round-bottom-flask":
      return (
        <g>
          <path d="M40 12 L56 12" />
          <path d="M42 12 L42 30 Q20 38 20 58 Q20 82 48 82 Q76 82 76 58 Q76 38 54 30 L54 12" />
          <path d="M28 58 Q48 54 68 58" opacity="0.6" />
        </g>
      );
    case "graduated-cylinder":
      return (
        <g>
          <path d="M30 12 L66 12" />
          <path d="M32 12 L32 80 Q32 86 38 86 L58 86 Q64 86 64 80 L64 12" />
          {/* Spout */}
          <path d="M64 14 Q70 14 72 18" />
          {/* Graduations */}
          {[24, 34, 44, 54, 64, 74].map((y) => (
            <path key={y} d={`M38 ${y} L46 ${y}`} />
          ))}
          <path d="M32 60 Q48 57 64 60" opacity="0.6" />
        </g>
      );
    case "burette":
      return (
        <g>
          <path d="M44 8 L60 8" />
          <path d="M46 8 L46 70 L52 70 L52 8" />
          {/* Graduations */}
          {[18, 26, 34, 42, 50, 58].map((y) => (
            <path key={y} d={`M48 ${y} L52 ${y}`} />
          ))}
          {/* Stopcock */}
          <circle cx="49" cy="74" r="4" />
          <path d="M45 74 L41 78" />
          {/* Tip */}
          <path d="M49 78 L49 88" />
          <path d="M48 88 L50 88" />
        </g>
      );
    case "volumetric-pipette":
      return (
        <g>
          <path d="M48 8 L48 30" />
          {/* Bulb */}
          <ellipse cx="48" cy="44" rx="11" ry="14" />
          <path d="M48 58 L48 86" />
          {/* Calibration line above bulb */}
          <path d="M44 26 L52 26" />
          {/* Tip */}
          <path d="M46 86 L50 86" />
        </g>
      );
    case "graduated-pipette":
      return (
        <g>
          <path d="M46 8 L50 8" />
          <path d="M46 8 L46 86 L50 86 L50 8" />
          {/* Graduations */}
          {[18, 28, 38, 48, 58, 68, 78].map((y) => (
            <path key={y} d={`M50 ${y} L56 ${y}`} />
          ))}
        </g>
      );
    case "thermometer":
      return (
        <g>
          <path d="M44 12 Q44 8 48 8 Q52 8 52 12 L52 64" />
          <path d="M44 12 L44 64" />
          <circle cx="48" cy="74" r="10" />
          {/* Scale */}
          {[20, 30, 40, 50, 60].map((y) => (
            <path key={y} d={`M52 ${y} L57 ${y}`} />
          ))}
          {/* Mercury column */}
          <path d="M47 50 L49 50 L49 74 L47 74 Z" fill="currentColor" opacity="0.5" />
        </g>
      );
    case "test-tube":
      return (
        <g>
          <path d="M36 10 L60 10" />
          <path d="M38 10 L38 72 Q38 86 48 86 Q58 86 58 72 L58 10" />
          <path d="M38 60 Q48 56 58 60" opacity="0.6" />
        </g>
      );
    case "watch-glass":
      return (
        <g>
          <path d="M14 50 Q48 22 82 50" />
          <path d="M14 50 Q48 60 82 50" />
        </g>
      );
    case "evaporating-dish":
      return (
        <g>
          <ellipse cx="48" cy="36" rx="34" ry="6" />
          <path d="M14 36 Q14 70 48 70 Q82 70 82 36" />
          {/* Spout */}
          <path d="M80 38 Q86 38 86 42" />
        </g>
      );
    case "crucible":
      return (
        <g>
          {/* Lid */}
          <path d="M28 20 L68 20 Q70 20 70 22 L70 24 L26 24 L26 22 Q26 20 28 20 Z" />
          {/* Body */}
          <path d="M22 30 L74 30 L66 80 Q66 84 60 84 L36 84 Q30 84 30 80 Z" />
          {/* Triangular feet hint */}
          <path d="M30 80 L34 86" />
          <path d="M66 80 L62 86" />
        </g>
      );
    case "mortar-pestle":
      return (
        <g>
          {/* Pestle */}
          <path d="M22 14 L34 26" />
          <circle cx="36" cy="28" r="4" />
          {/* Mortar */}
          <path d="M16 50 Q48 36 80 50" />
          <path d="M16 50 Q24 84 48 84 Q72 84 80 50" />
        </g>
      );
    case "bunsen-burner":
      return (
        <g>
          {/* Flame */}
          <path d="M48 10 Q42 20 44 30 Q46 24 48 24 Q50 30 52 30 Q54 22 48 10 Z" />
          {/* Barrel */}
          <path d="M44 34 L52 34 L52 66 L44 66 Z" />
          {/* Air collar */}
          <path d="M42 50 L54 50" />
          <path d="M42 56 L54 56" />
          {/* Gas inlet */}
          <path d="M52 70 L70 70" />
          {/* Base */}
          <path d="M30 70 L66 70 L62 84 L34 84 Z" />
        </g>
      );
    case "hot-plate":
      return (
        <g>
          {/* Top plate */}
          <rect x="14" y="46" width="68" height="10" rx="2" />
          {/* Body */}
          <rect x="14" y="56" width="68" height="22" rx="2" />
          {/* Dial */}
          <circle cx="68" cy="67" r="5" />
          <path d="M68 67 L71 65" />
          {/* Display */}
          <rect x="22" y="62" width="22" height="10" />
          {/* Coil hint on plate */}
          <path d="M22 51 Q34 49 46 51 Q58 53 70 51" opacity="0.6" />
          {/* Feet */}
          <path d="M20 78 L20 84" />
          <path d="M76 78 L76 84" />
        </g>
      );
    case "tripod":
      return (
        <g>
          {/* Top ring */}
          <ellipse cx="48" cy="32" rx="22" ry="5" />
          {/* Legs */}
          <path d="M28 34 L18 84" />
          <path d="M48 36 L48 86" />
          <path d="M68 34 L78 84" />
        </g>
      );
    case "wire-gauze":
      return (
        <g>
          {/* Outer pad */}
          <rect x="14" y="34" width="68" height="28" rx="2" />
          {/* Mesh lines */}
          {[40, 46, 52, 58].map((y) => (
            <path key={`h${y}`} d={`M16 ${y} L80 ${y}`} opacity="0.7" />
          ))}
          {[24, 32, 40, 48, 56, 64, 72].map((x) => (
            <path key={`v${x}`} d={`M${x} 36 L${x} 60`} opacity="0.7" />
          ))}
          {/* Ceramic centre */}
          <circle cx="48" cy="48" r="8" fill="currentColor" opacity="0.15" />
        </g>
      );
    case "retort-stand":
      return (
        <g>
          {/* Base */}
          <rect x="14" y="80" width="50" height="6" rx="1" />
          {/* Rod */}
          <path d="M22 80 L22 12" />
          {/* Boss */}
          <rect x="22" y="36" width="10" height="10" />
          {/* Clamp arm */}
          <path d="M32 41 L62 41" />
          {/* Clamp jaws */}
          <path d="M62 34 L62 48" />
          <path d="M62 36 L74 36" />
          <path d="M62 46 L74 46" />
          <path d="M74 36 L74 46" />
        </g>
      );
    case "test-tube-rack":
      return (
        <g>
          {/* Top rail with holes */}
          <rect x="10" y="30" width="76" height="10" rx="2" />
          {[22, 36, 50, 64, 78].map((x) => (
            <circle key={x} cx={x} cy="35" r="3" />
          ))}
          {/* Tubes */}
          {[22, 36, 50, 64, 78].map((x) => (
            <g key={`t${x}`}>
              <path d={`M${x - 4} 38 L${x - 4} 64 Q${x - 4} 70 ${x} 70 Q${x + 4} 70 ${x + 4} 64 L${x + 4} 38`} />
            </g>
          ))}
          {/* Base */}
          <rect x="10" y="76" width="76" height="6" rx="1" />
        </g>
      );
    case "crucible-tongs":
      return (
        <g>
          {/* Pivot */}
          <circle cx="48" cy="48" r="3" />
          {/* Handles (open scissor) */}
          <path d="M48 48 L20 22" />
          <path d="M48 48 L20 74" />
          {/* Jaws */}
          <path d="M48 48 L78 30" />
          <path d="M48 48 L78 66" />
          {/* Curved tips */}
          <path d="M78 30 Q86 38 78 46" />
          <path d="M78 66 Q86 58 78 50" />
        </g>
      );
    case "separating-funnel":
      return (
        <g>
          {/* Stopper */}
          <path d="M40 8 L56 8 L54 14 L42 14 Z" />
          {/* Neck */}
          <path d="M44 14 L44 26 L52 26 L52 14" />
          {/* Pear body */}
          <path d="M44 26 Q18 30 24 56 Q30 78 48 78 Q66 78 72 56 Q78 30 52 26" />
          {/* Layer line */}
          <path d="M28 56 Q48 50 68 56" opacity="0.6" />
          {/* Stopcock */}
          <circle cx="48" cy="80" r="3" />
          <path d="M44 80 L40 84" />
          <path d="M48 83 L48 90" />
        </g>
      );
    case "filter-funnel":
      return (
        <g>
          {/* Cone */}
          <path d="M16 18 L80 18 L52 60 L44 60 Z" />
          {/* Stem */}
          <path d="M44 60 L44 86 L52 86 L52 60" />
          {/* Filter paper line */}
          <path d="M22 24 L74 24" opacity="0.5" />
        </g>
      );
    case "condenser":
      return (
        <g>
          {/* Inner tube */}
          <path d="M10 46 L86 46" />
          <path d="M10 54 L86 54" />
          {/* Outer jacket */}
          <path d="M16 38 L80 38 L80 62 L16 62 Z" />
          {/* Water inlet/outlet */}
          <path d="M22 38 L22 26 L18 26 L18 22 L26 22 L26 26 L22 26" />
          <path d="M74 62 L74 74 L70 74 L70 78 L78 78 L78 74 L74 74" />
          {/* End ports */}
          <path d="M10 42 L10 58" />
          <path d="M86 42 L86 58" />
        </g>
      );
    case "desiccator":
      return (
        <g>
          {/* Lid */}
          <path d="M14 30 Q48 14 82 30" />
          {/* Lid knob */}
          <circle cx="48" cy="20" r="3" />
          {/* Body */}
          <path d="M14 30 L14 70 Q14 84 48 84 Q82 84 82 70 L82 30" />
          {/* Plate inside */}
          <path d="M22 50 L74 50" strokeDasharray="3 2" />
          {/* Desiccant beads */}
          {[
            [30, 64], [38, 66], [46, 64], [54, 66], [62, 64],
          ].map(([cx, cy], i) => (
            <circle key={i} cx={cx} cy={cy} r="2" />
          ))}
        </g>
      );
    default:
      return (
        <g>
          <rect x="20" y="20" width="56" height="56" rx="4" />
          <path d="M48 36 L48 60" />
        </g>
      );
  }
}
